/**
 * Demo reset — wipes the state accumulated while testing as the `demo-coach`
 * account. Scoped to data that demo mode actually uses:
 *
 *   - Tables with a TEXT `coach_id` (the video-import pipeline).
 *   - Teams with `coach_id IS NULL` — the demo pattern. Sidebar loads the
 *     first team it finds, so unless we nuke the null-coach team it gets
 *     re-picked up on reload. Children of that team (roster, principles,
 *     curriculum, sessions, brain data, conversations, etc.) go with it.
 *   - localStorage keys prefixed `4cm_`.
 *
 * Conservative: never touches a team with a real auth UUID in coach_id. If a
 * real coach is signed in and has their own data, this function leaves it
 * alone.
 *
 * Ported from web1's demoReset.ts (apps/web/src/lib/demoReset.ts) — kept in
 * sync deliberately; same scope, same PRESERVE list, same cascade order.
 */
import { supabase } from './supabase';
import { DEMO_COACH_ID } from '@4cm/core/demoIdentity';

export { DEMO_COACH_ID };

const DEMO_VIDEO_TABLES = [
  'video_capture_sessions',
  'video_calibrations',
] as const;

/** Tables keyed by UUID `coach_id` (not TEXT) belonging to the demo coach. */
const DEMO_COACH_SCOPED_TABLES: readonly string[] = [];

/**
 * Children of `teams` that need wiping when we delete a demo team.
 * Ordered leaf-first so deletes don't trip FK constraints even if any
 * table has ON DELETE RESTRICT (mostly CASCADE, but explicit per-table
 * counts aid the summary modal).
 */
const TEAM_CHILDREN = [
  'analytics_snapshots',
  'brain_memories',
  'brain_suggestions',
  'corner_weights',
  'curriculum_plans',
  'games',
  'principles',
  'sessions',
  'systems_of_play',
  'team_players',
] as const;

export interface DemoResetResult {
  ok: boolean;
  deleted: Record<string, number>;
  teamsRemoved: number;
  localStorageKeysCleared: number;
  errors: string[];
}

export async function resetDemoData(): Promise<DemoResetResult> {
  const result: DemoResetResult = {
    ok: true,
    deleted: {},
    teamsRemoved: 0,
    localStorageKeysCleared: 0,
    errors: [],
  };

  const { data: nullTeams, error: nullErr } = await supabase
    .from('teams')
    .select('id')
    .is('coach_id', null);
  if (nullErr) { result.errors.push(`teams null (select): ${nullErr.message}`); result.ok = false; }

  const { data: demoIdTeams, error: demoErr } = await supabase
    .from('teams')
    .select('id')
    .eq('coach_id', DEMO_COACH_ID);
  if (demoErr) { result.errors.push(`teams demo (select): ${demoErr.message}`); result.ok = false; }

  const teamIds = Array.from(new Set([
    ...(nullTeams ?? []).map((t: { id: string }) => t.id),
    ...(demoIdTeams ?? []).map((t: { id: string }) => t.id),
  ]));

  if (teamIds.length > 0) {
    const { data: sessRows } = await supabase
      .from('sessions')
      .select('id')
      .in('team_id', teamIds);
    const sessionIds = (sessRows ?? []).map((s: { id: string }) => s.id);

    const { data: gameRows } = await supabase
      .from('games')
      .select('id')
      .in('team_id', teamIds);
    const gameIds = (gameRows ?? []).map((g: { id: string }) => g.id);

    if (sessionIds.length > 0) {
      await cascadeDelete(result, 'session_drills', 'session_id', sessionIds);
      await cascadeDelete(result, 'ratings', 'session_id', sessionIds);
      await cascadeDelete(result, 'video_tags', 'session_id', sessionIds);
    }
    if (gameIds.length > 0) {
      await cascadeDelete(result, 'ratings', 'game_id', gameIds);
      await cascadeDelete(result, 'video_tags', 'game_id', gameIds);
    }

    const { data: convs } = await supabase
      .from('coach_conversations')
      .select('id')
      .in('team_id', teamIds);
    const convIds = (convs ?? []).map((c: { id: string }) => c.id);
    if (convIds.length > 0) {
      await cascadeDelete(result, 'coach_messages', 'conversation_id', convIds);
      await cascadeDelete(result, 'coach_conversations', 'id', convIds);
    }

    for (const table of TEAM_CHILDREN) {
      await cascadeDelete(result, table, 'team_id', teamIds);
    }

    const { data: tDel, error: tErr } = await supabase
      .from('teams')
      .delete()
      .in('id', teamIds)
      .select('id');
    if (tErr) {
      result.errors.push(`teams: ${tErr.message}`);
      result.ok = false;
    } else {
      result.teamsRemoved = tDel?.length ?? 0;
    }
  }

  const LEGACY_DEMO_ID = 'demo-coach';
  for (const table of DEMO_VIDEO_TABLES) {
    const { data, error } = await supabase
      .from(table)
      .delete()
      .in('coach_id', [DEMO_COACH_ID, LEGACY_DEMO_ID])
      .select('id');
    if (error) {
      result.errors.push(`${table}: ${error.message}`);
      result.ok = false;
    } else {
      result.deleted[table] = data?.length ?? 0;
    }
  }

  for (const table of DEMO_COACH_SCOPED_TABLES) {
    const { data, error } = await supabase
      .from(table)
      .delete()
      .eq('coach_id', DEMO_COACH_ID)
      .select('id');
    if (error) {
      result.errors.push(`${table}: ${error.message}`);
      result.ok = false;
    } else {
      result.deleted[table] = data?.length ?? 0;
    }
  }

  const PRESERVE: ReadonlySet<string> = new Set([
    '4cm_anthropic_model',
    '4cm_anthropic_model_map',
    '4cm_model_panel',
    '4cm_debug',
    '4cm_debug_level',
    '4cm_debug_buffer',
    '4cm_debug_panel',
    // Web2-specific keys we want to survive a reset:
    '4cm_calibration',
    '4cm_theme_accent',
    '4cm_voice_prefs',
  ]);
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('4cm_') && !PRESERVE.has(k)) toRemove.push(k);
    }
    for (const k of toRemove) localStorage.removeItem(k);
    result.localStorageKeysCleared = toRemove.length;
  } catch (e) {
    result.errors.push(`localStorage: ${e instanceof Error ? e.message : String(e)}`);
  }

  return result;
}

async function cascadeDelete(
  result: DemoResetResult,
  table: string,
  col: string,
  ids: readonly string[],
): Promise<void> {
  if (ids.length === 0) return;
  const { data, error } = await supabase
    .from(table)
    .delete()
    .in(col, ids as string[])
    .select('id');
  if (error) {
    result.errors.push(`${table} by ${col}: ${error.message}`);
    result.ok = false;
    return;
  }
  result.deleted[table] = (result.deleted[table] ?? 0) + (data?.length ?? 0);
}

export function summarizeResetResult(r: DemoResetResult): string {
  const lines: string[] = [];
  if (r.teamsRemoved > 0) lines.push(`teams removed: ${r.teamsRemoved}`);
  for (const [t, n] of Object.entries(r.deleted)) {
    if (n > 0) lines.push(`${t}: ${n}`);
  }
  lines.push(`localStorage keys: ${r.localStorageKeysCleared}`);
  if (r.errors.length) lines.push(`\nErrors:\n${r.errors.join('\n')}`);
  return lines.join('\n');
}
